import { useState, useEffect } from 'react'
import { isAxiosError } from 'axios'
import { Box, Button } from '@mui/material'
import {
  carePlanTemplatesAssign,
  CarePlanTemplatesAssignCreateBodyParams,
  CarePlanTemplatesPreviewRetrieveResponse,
  CarePlanTemplatesAssignCreateResponse,
} from '@fireflyhealth/api'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import { CarePlanModalTask } from './useCarePlanModal'
import { setDueDateToEOD } from '~/shared/lib/date'
import { listCarePlansQueryKey } from '../CarePlanService'
import type { PatientUser } from '~/shared/types'
import { logger } from '~/shared/lib/logger'

interface ICreateFromTemplateActionsProps {
  closeModal: () => void
  patient: PatientUser
  handleModalAction: (carePlan: CarePlanTemplatesAssignCreateResponse) => void
  tasks: CarePlanModalTask[]
  readyToSaveFromTemplateId: null | number
  saving: boolean
  setSaving: (arg0: boolean) => void
  allTemplateTasksComplete: boolean
  title: string
  notes: string
  previewData?: CarePlanTemplatesPreviewRetrieveResponse
  internalOnly: boolean
}

const CreateFromTemplateActions = (props: ICreateFromTemplateActionsProps) => {
  const {
    tasks,
    readyToSaveFromTemplateId,
    saving,
    setSaving,
    allTemplateTasksComplete,
    previewData,
    internalOnly,
  } = props

  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setSaving(isLoading)
  }, [isLoading, setSaving])

  const saveDisabled = () =>
    saving || isLoading || !readyToSaveFromTemplateId || !allTemplateTasksComplete

  const handleSave = async () => {
    if (saveDisabled() || !readyToSaveFromTemplateId) return

    setIsLoading(true)
    try {
      const tasksPayload: CarePlanTemplatesAssignCreateBodyParams['tasks'] = previewData
        ?.task_previews?.length
        ? tasks.map(task => ({
            title: task.title,
            due_date: setDueDateToEOD(task.due_date),
            owner_group: task.owner_group,
            priority: task.priority,
            task_template_pk: task.task_template_pk,
            is_complete: task.is_complete ?? false,
          }))
        : []

      const response = await carePlanTemplatesAssign.create(
        { id: readyToSaveFromTemplateId },
        {
          data: {
            user_id: props.patient.id,
            tasks: tasksPayload,
            title: props.title || undefined,
            notes: props.notes || undefined,
            visible_to_patient: !internalOnly,
          },
        },
        { invalidateQueryKeys: [listCarePlansQueryKey(props.patient.id)] },
      )

      const createdCarePlan = response.data
      props.handleModalAction(createdCarePlan)
      dispatch(queueNotification({ variant: 'success', message: 'Created care plan' }))
      props.closeModal()
    } catch (e) {
      const errorMessage =
        (isAxiosError<{ error?: string }>(e) ? e.response?.data?.error : null) ||
        'Failed to assign care plan'
      dispatch(queueNotification({ variant: 'error', message: errorMessage }))
      logger.error('[CarePlan] assign care plan', e)
    } finally {
      setIsLoading(false)
    }
  }

  const getButtonText = () => {
    if (internalOnly) {
      return 'Save'
    }
    return 'Save & Send to Member'
  }

  return (
    <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button onClick={props.closeModal} disabled={saving} sx={{ px: '1em', mr: 1 }}>
        Cancel
      </Button>
      <Button
        disabled={saveDisabled()}
        sx={{ px: '1em', boxShadow: 'none' }}
        color="primary"
        variant="contained"
        onClick={() => void handleSave()}
      >
        {getButtonText()}
      </Button>
    </Box>
  )
}

export default CreateFromTemplateActions
